import { ReloadOutlined } from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import {
  type IReportProductLanguage,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { Button, Divider, Select, Space, Spin, Switch } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import slugify from 'slugify';

import { ENotificationSeverity } from '../../admin-types';
import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import {
  AutomatProgressBar,
  type IAutomatProgress,
} from '../fragments/automat-progress-bar';
import { openGoogleTranslateLink } from '../fragments/google-translate-link';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useAutomatDocumentTitle } from '../hooks/use-automat-document-title';
import {
  notificationContext,
  type OpenNotificationType,
} from '../notification';
import { type AppDispatch, type RootState } from '../reducers';
import { namesDescriptionsSlugsUpdate } from '../reducers/product/reducer';
import { reportProductLanguagesLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

interface DataType extends IReportProductLanguage {
  key: string;
}

const LIMIT_OPTIONS = [100, 250, 500, 1000, 2000].map((value) => ({
  value,
  label: `${value} products`,
}));

async function startAutomat(
  dataSource: DataType[],
  dispatch: AppDispatch,
  setRunning: React.Dispatch<React.SetStateAction<boolean>>,
  setProgress: React.Dispatch<React.SetStateAction<IAutomatProgress | null>>,
  openNotification: OpenNotificationType,
): Promise<void> {
  let total = 0;
  for (const record of dataSource) {
    const [n] = record.translation.names.filter(notEmpty);
    const [d] = record.translation.descriptions.filter(notEmpty);
    const perLang =
      (n && n.value.length >= 2 ? 1 : 0) + (d && d.value.length >= 2 ? 1 : 0);
    total += record.languageType.missingCodes.length * perLang;
  }

  setRunning(true);
  const startedAt = Date.now();
  setProgress({ current: 0, total, startedAt });
  let current = 0;

  for (const record of dataSource) {
    const names: ITranslation[] = [];
    const descriptions: ITranslation[] = [];
    const slugs: ITranslation[] = [];

    for (const languageTypeCodeMissing of record.languageType.missingCodes) {
      const [translationName] = record.translation.names.filter(notEmpty);
      const [translationDescription] =
        record.translation.descriptions.filter(notEmpty);

      let capturedName: string | undefined;
      let capturedDescription: string | undefined;

      if (translationName && translationName.value.length >= 2) {
        await new Promise<void>((resolve): void => {
          window.addEventListener(
            'focus',
            async (): Promise<void> => {
              const text: string = await navigator.clipboard.readText();
              const tt: string = text.trim();

              if (tt) {
                capturedName = tt;
              }

              await navigator.clipboard.writeText('');

              resolve();
            },
            { once: true },
          );

          openGoogleTranslateLink(
            translationName.code,
            languageTypeCodeMissing,
            translationName.value,
          );
        });

        current++;
        setProgress({ current, total, startedAt });
      }

      if (translationDescription && translationDescription.value.length >= 2) {
        await new Promise<void>((resolve) => {
          window.addEventListener(
            'focus',
            async (): Promise<void> => {
              const text: string = await navigator.clipboard.readText();
              const tt: string = text.trim();

              if (tt) {
                capturedDescription = tt;
              }

              await navigator.clipboard.writeText('');

              resolve();
            },
            { once: true },
          );

          openGoogleTranslateLink(
            translationDescription.code,
            languageTypeCodeMissing,
            translationDescription.value,
          );
        });

        current++;
        setProgress({ current, total, startedAt });
      }

      const nameExpected = (translationName?.value.length ?? 0) >= 2;
      const descriptionExpected =
        (translationDescription?.value.length ?? 0) >= 2;
      const nameOK = !nameExpected || Boolean(capturedName);
      const descriptionOK =
        !descriptionExpected || Boolean(capturedDescription);

      if (nameOK && descriptionOK && (capturedName || capturedDescription)) {
        if (capturedName) {
          names.push({ code: languageTypeCodeMissing, value: capturedName });
          slugs.push({
            code: languageTypeCodeMissing,
            value: slugify(capturedName, { lower: true, strict: true }),
          });
        }

        if (capturedDescription) {
          descriptions.push({
            code: languageTypeCodeMissing,
            value: capturedDescription,
          });
        }
      } else if (!nameOK || !descriptionOK) {
        openNotification('topRight', {
          severity: ENotificationSeverity.WARNING,
          title: 'Incomplete translation skipped',
          description: `Product ${record.product.uuid} (${languageTypeCodeMissing}): failed to copy ${!nameOK ? 'name' : 'description'}.`,
        });
      }
    }

    if (names.length === 0) {
      continue;
    }

    dispatch(
      namesDescriptionsSlugsUpdate(
        record.product.uuid,
        names,
        descriptions,
        slugs,
        [],
        [],
      ),
    );
  }

  setRunning(false);
  setProgress(null);
}

function ReportProductLanguages(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { openNotification } = useContext(notificationContext);
  const reportProductLanguages = useSelector(
    (s: RootState) => s.Report.productLanguages,
  );

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [isRunning, setRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<IAutomatProgress | null>(null);
  const [onlyActiveLanguages, setOnlyActiveLanguages] = useState<boolean>(true);
  const [limit, setLimit] = useState<number>(100);

  useEffect((): void => {
    if (!reportProductLanguages) {
      return;
    }

    const columnsUE: ColumnsType<object> = [
      {
        title: 'UUID',
        dataIndex: ['product', 'uuid'],
        render: (_text, record: DataType): React.ReactElement => (
          <Link
            to={`/product/${record.product.uuid}`}
            openInNewTab
            label={record.product.uuid}
          />
        ),
      },
      {
        title: 'Name',
        dataIndex: ['translation', 'names', 0, 'value'],
        render: (_text, record: DataType): string => {
          const [name] = record.translation.names.filter(notEmpty);

          return `[${name?.code}] ${name?.value}`;
        },
      },
      {
        title: 'Missing languages',
        dataIndex: ['languageType', 'missingCodes'],
        render: (_text, record: DataType): string =>
          record.languageType.missingCodes.join(', '),
      },
    ];

    setColumns(columnsUE);

    const dataSourceUE = reportProductLanguages.map((item) => ({
      ...item,
      key: item.product.uuid,
    }));

    setDataSource(dataSourceUE);
  }, [reportProductLanguages]);

  useAutomatDocumentTitle(isRunning, progress);

  return (
    <>
      <DetailPageHeader title="Report product languages" />

      <Space size={SPACING.lg} align="start">
        <Switch
          checkedChildren="Active languages"
          unCheckedChildren="All languages"
          checked={onlyActiveLanguages}
          onChange={setOnlyActiveLanguages}
        />
        <Select
          value={limit}
          onChange={setLimit}
          options={LIMIT_OPTIONS}
          style={{ width: 150 }}
        />
        <Button
          type="primary"
          onClick={(): void => {
            dispatch(reportProductLanguagesLoad(onlyActiveLanguages, limit));
          }}
          disabled={!reportProductLanguages}
        >
          <ReloadOutlined />
        </Button>
        <Button
          type="primary"
          onClick={(): void => {
            startAutomat(
              dataSource,
              dispatch,
              setRunning,
              setProgress,
              openNotification,
            );
          }}
          disabled={
            !reportProductLanguages || isRunning || dataSource.length === 0
          }
        >
          GT Automat
        </Button>
        <Spin size="small" spinning={isRunning} />
      </Space>

      <AutomatProgressBar isRunning={isRunning} progress={progress} />

      <Divider />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={!reportProductLanguages}
      />
    </>
  );
}

export default ReportProductLanguages;
